import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, X, Zap, Star, Crown } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #474545;
  padding: 80px 20px 60px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: #FAECEC;
  opacity: 0.8;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const BillingToggle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 50px;
`;

const ToggleLabel = styled.span`
  color: #FAECEC;
  font-size: 1rem;
  opacity: ${props => props.active ? 1 : 0.6};
  font-weight: ${props => props.active ? 600 : 400};
  transition: all 0.3s ease;
`;

const ToggleSwitch = styled.button`
  width: 60px;
  height: 32px;
  background: ${props => props.isAnnual ? 'linear-gradient(135deg, #0097B2 0%, #007a94 100%)' : 'rgba(0, 151, 178, 0.3)'};
  border: none;
  border-radius: 16px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    top: 4px;
    left: ${props => props.isAnnual ? '32px' : '4px'};
    width: 24px;
    height: 24px;
    background: #FAECEC;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
`;

const SaveBadge = styled.span`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: #FAECEC;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    max-width: 450px;
    margin: 0 auto;
  }
`;

const PlanCard = styled(motion.div)`
  background: ${props => props.popular 
    ? 'linear-gradient(135deg, rgba(0, 151, 178, 0.2) 0%, rgba(250, 236, 236, 0.1) 100%)'
    : 'linear-gradient(135deg, rgba(0, 151, 178, 0.08) 0%, rgba(250, 236, 236, 0.05) 100%)'};
  border: ${props => props.popular 
    ? '2px solid rgba(0, 151, 178, 0.5)'
    : '1px solid rgba(0, 151, 178, 0.2)'};
  border-radius: 24px;
  padding: 40px 30px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 151, 178, 0.2);
  }

  ${props => props.popular && `
    transform: scale(1.05);
    
    @media (max-width: 992px) {
      transform: scale(1);
    }
  `}
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  color: #FAECEC;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PlanIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 8px 20px rgba(0, 151, 178, 0.3);
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #FAECEC;
  margin-bottom: 8px;
`;

const PriceContainer = styled.div`
  margin-bottom: 24px;
`;

const Price = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #0097B2;
  line-height: 1;
  margin-bottom: 4px;

  span {
    font-size: 1rem;
    font-weight: 400;
    color: #FAECEC;
    opacity: 0.7;
  }
`;

const BilledText = styled.p`
  color: #FAECEC;
  opacity: 0.6;
  font-size: 0.9rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #FAECEC;
  font-size: 0.95rem;
  margin-bottom: 14px;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const CTAButton = styled(motion.a)`
  display: block;
  width: 100%;
  padding: 16px 24px;
  background: ${props => props.popular 
    ? 'linear-gradient(135deg, #0097B2 0%, #007a94 100%)'
    : 'transparent'};
  color: #FAECEC;
  border: ${props => props.popular ? 'none' : '2px solid #0097B2'};
  border-radius: 50px;
  font-family: 'Comfortaa', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 151, 178, 0.3);
  }
`;

/* Comparison Table Styles */
const ComparisonSection = styled.section`
  margin-top: 100px;
`;

const ComparisonTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 50px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 20px;
  border: 1px solid rgba(0, 151, 178, 0.2);
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.05) 0%, rgba(250, 236, 236, 0.02) 100%);

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 151, 178, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #0097B2;
    border-radius: 4px;
  }
`;

const ComparisonTable = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.15) 0%, rgba(0, 151, 178, 0.08) 100%);
`;

const TableHeadRow = styled.tr``;

const TableHeadCell = styled.th`
  padding: 24px 20px;
  text-align: ${props => props.feature ? 'left' : 'center'};
  color: #FAECEC;
  font-weight: 600;
  font-size: 1.1rem;
  border-bottom: 1px solid rgba(0, 151, 178, 0.2);
  min-width: ${props => props.feature ? '250px' : '150px'};

  &:not(:last-child) {
    border-right: 1px solid rgba(0, 151, 178, 0.1);
  }
`;

const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PlanHeaderName = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: #0097B2;
`;

const PlanHeaderPrice = styled.span`
  font-size: 0.9rem;
  font-weight: 400;
  color: #FAECEC;
  opacity: 0.8;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background 0.3s ease;

  &:hover {
    background: rgba(0, 151, 178, 0.05);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 151, 178, 0.1);
  }
`;

const TableCell = styled.td`
  padding: 18px 20px;
  text-align: ${props => props.feature ? 'left' : 'center'};
  color: #FAECEC;
  font-size: 0.95rem;
  vertical-align: middle;

  &:not(:last-child) {
    border-right: 1px solid rgba(0, 151, 178, 0.05);
  }
`;

const FeatureName = styled.span`
  color: #FAECEC;
  opacity: 0.9;
`;

const FeatureValue = styled.span`
  color: #0097B2;
  font-weight: 600;
`;

const CheckIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border-radius: 50%;
`;

const XIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 107, 107, 0.2);
  border-radius: 50%;
`;

const CategoryRow = styled.tr`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.1) 0%, rgba(0, 151, 178, 0.05) 100%);
`;

const CategoryCell = styled.td`
  padding: 14px 20px;
  color: #0097B2;
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InfoSection = styled.div`
  margin-top: 80px;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #FAECEC;
  margin-bottom: 16px;
`;

const InfoText = styled.p`
  color: #FAECEC;
  opacity: 0.8;
  line-height: 1.7;
  font-size: 1rem;
`;

const Plans = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: 'f01i Plus',
      icon: <Zap size={28} color="#FAECEC" />,
      monthlyPrice: 10,
      annualPrice: 8,
      annualTotal: 96,
      popular: false,
      features: [
        '10 requests per day to f01i Agent',
        'Create & manage 3 trading bots',
        'Connect to ready-made template bots',
        'Use your own API Keys',
        '1,000 executions/month'
      ]
    },
    {
      name: 'f01i Gamechanger',
      icon: <Star size={28} color="#FAECEC" />,
      monthlyPrice: 25,
      annualPrice: 20,
      annualTotal: 240,
      popular: true,
      features: [
        '30 requests per day to f01i Agent',
        'Create & manage 10 trading bots',
        'Connect to ready-made template bots',
        'f01i Blocks — comprehensive AI strategies',
        'Use your own API Keys',
        '10,000 executions/month'
      ]
    },
    {
      name: 'f01i Pro',
      icon: <Crown size={28} color="#FAECEC" />,
      monthlyPrice: 49,
      annualPrice: 39,
      annualTotal: 468,
      popular: true,
      features: [
        '70 requests per day to f01i Agent',
        'Create & manage 30 trading bots',
        'Connect to ready-made template bots',
        'f01i Blocks — comprehensive AI strategies',
        'Pro Agents Templates (f01i Consensus Agent + f01i Trading Team)',
        'Use your own API Keys',
        '30,000 executions/month',
        'Priority support'
      ]
    }
  ];

  const comparisonData = [
    { category: 'f01i Agent' },
    { feature: 'Daily requests to AI Agent', plus: '10', gamechanger: '30', pro: '70' },
    { feature: 'Technical analysis', plus: true, gamechanger: true, pro: true },
    { feature: 'Chart generation', plus: true, gamechanger: true, pro: true },
    { feature: 'Market insights', plus: true, gamechanger: true, pro: true },
    
    { category: 'Trading Bots' },
    { feature: 'Number of trading bots', plus: '3', gamechanger: '10', pro: '30' },
    { feature: 'AI Bot Chat Builder (LLMs)', plus: true, gamechanger: true, pro: true },
    { feature: 'Ready-made template bots', plus: true, gamechanger: true, pro: true },
    { feature: 'Manual bot configuration', plus: true, gamechanger: true, pro: true },
    { feature: 'Monthly executions', plus: '1,000', gamechanger: '10,000', pro: '30,000' },
    
    { category: 'f01i Blocks' },
    { feature: 'Custom AI trading strategies', plus: false, gamechanger: true, pro: true },
    { feature: 'Monthly executions', plus: false, gamechanger: '10,000', pro: '30,000' },
    
    { category: 'API & Integrations' },
    { feature: 'Use your own LLM API keys', plus: true, gamechanger: true, pro: true },
    { feature: 'Multi-exchange support (Bybit, Binance, OKX, Coinbase, MEXC, Crypto.com, Hyperliquid)', plus: true, gamechanger: true, pro: true },
    { feature: 'API Trading tools (Coinglass, CoinMarketCap, CoinGecko etc.)', plus: false, gamechanger: true, pro: true },
    
    { category: 'Support & Extras' },
    { feature: 'Email support', plus: true, gamechanger: true, pro: true },
    { feature: 'Priority support', plus: false, gamechanger: false, pro: true },
    { feature: 'Zero trading commissions', plus: true, gamechanger: true, pro: true },

    { category: 'Pro Agents Templates' },
    { feature: 'f01i Consensus Agent (4 AI models + Consensus Judge)', plus: false, gamechanger: false, pro: true },
    { feature: 'f01i Trading Team (Research + Auto Positions via Telegram)', plus: false, gamechanger: false, pro: true },
  ];

  const renderCellValue = (value) => {
    if (value === true) {
      return (
        <CheckIcon>
          <Check size={16} color="#FAECEC" />
        </CheckIcon>
      );
    }
    if (value === false) {
      return (
        <XIcon>
          <X size={16} color="#ff6b6b" />
        </XIcon>
      );
    }
    return <FeatureValue>{value}</FeatureValue>;
  };

  return (
    <PageContainer>
      <Helmet>
        <title>AI Crypto Trading Bot Builder - Zero Commissions, Pricing</title>
        <meta name="description" content="Choose your f01i.ai plan. Build AI-powered crypto trading bots with zero commissions. Plans from $8/month — connect to Bybit, Binance, OKX and more." />
        <link rel="canonical" href="https://f01i.ai/pricing" />
      </Helmet>
      <Container>
        <BackLink to="/">
          <ArrowLeft size={20} />
          Back to Home
        </BackLink>

        <Header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>Choose Your Plan</Title>
          <Subtitle>
            No trading commissions. Pay only for executions. Start building your automated trading strategy today.
          </Subtitle>
        </Header>

        <BillingToggle>
          <ToggleLabel active={!isAnnual}>Monthly</ToggleLabel>
          <ToggleSwitch 
            isAnnual={isAnnual} 
            onClick={() => setIsAnnual(!isAnnual)}
          />
          <ToggleLabel active={isAnnual}>Annual</ToggleLabel>
          <SaveBadge>Save 20%</SaveBadge>
        </BillingToggle>

        <PlansGrid>
          {plans.map((plan, index) => (
            <PlanCard
              key={index}
              popular={plan.popular}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              {plan.popular && (
                <PopularBadge>
                  <Star size={14} fill="#FAECEC" />
                  Popular
                </PopularBadge>
              )}
              
              <PlanIcon>{plan.icon}</PlanIcon>
              <PlanName>{plan.name}</PlanName>
              
              <PriceContainer>
                <Price>
                  ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  <span>/month</span>
                </Price>
                {isAnnual && (
                  <BilledText>${plan.annualTotal} billed annually</BilledText>
                )}
              </PriceContainer>

              <FeaturesList>
                {plan.features.map((feature, i) => (
                  <FeatureItem key={i}>
                    <Check size={18} color="#0097B2" />
                    {feature}
                  </FeatureItem>
                ))}
              </FeaturesList>

              <CTAButton
                href="https://f01i.app/login"
                popular={plan.popular}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started
              </CTAButton>
            </PlanCard>
          ))}
        </PlansGrid>

        {/* Comparison Table */}
        <ComparisonSection>
          <ComparisonTitle>Compare All Features</ComparisonTitle>
          
          <TableWrapper>
            <ComparisonTable>
              <TableHead>
                <TableHeadRow>
                  <TableHeadCell feature>Features</TableHeadCell>
                  <TableHeadCell>
                    <PlanHeader>
                      <PlanHeaderName>f01i Plus</PlanHeaderName>
                      <PlanHeaderPrice>${isAnnual ? '8' : '10'}/mo</PlanHeaderPrice>
                    </PlanHeader>
                  </TableHeadCell>
                  <TableHeadCell>
                    <PlanHeader>
                      <PlanHeaderName>f01i Gamechanger</PlanHeaderName>
                      <PlanHeaderPrice>${isAnnual ? '20' : '25'}/mo</PlanHeaderPrice>
                    </PlanHeader>
                  </TableHeadCell>
                  <TableHeadCell>
                    <PlanHeader>
                      <PlanHeaderName>f01i Pro</PlanHeaderName>
                      <PlanHeaderPrice>${isAnnual ? '39' : '49'}/mo</PlanHeaderPrice>
                    </PlanHeader>
                  </TableHeadCell>
                </TableHeadRow>
              </TableHead>
              <TableBody>
                {comparisonData.map((row, index) => {
                  if (row.category) {
                    return (
                      <CategoryRow key={index}>
                        <CategoryCell colSpan={4}>{row.category}</CategoryCell>
                      </CategoryRow>
                    );
                  }
                  return (
                    <TableRow key={index}>
                      <TableCell feature>
                        <FeatureName>{row.feature}</FeatureName>
                      </TableCell>
                      <TableCell>{renderCellValue(row.plus)}</TableCell>
                      <TableCell>{renderCellValue(row.gamechanger)}</TableCell>
                      <TableCell>{renderCellValue(row.pro)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </ComparisonTable>
          </TableWrapper>
        </ComparisonSection>

        <InfoSection>
          <InfoTitle>Why f01i.ai?</InfoTitle>
          <InfoText>
            Unlike other platforms, we charge <strong>zero trading commissions</strong>. 
            You only pay for bot executions, keeping more profit in your pocket. 
            All plans include the ability to use your own LLM API keys for maximum flexibility and cost control.
          </InfoText>
        </InfoSection>
      </Container>
    </PageContainer>
  );
};

export default Plans;
